import { Loop } from './Loop.js';
import { Quad } from './Quad.js';
import { easeOutSine, easeOutCirc } from '../utils/easings.js';
import { diceRoll } from '../utils/sableUtils.js';

/********************************/
/*          💎 GEM 💎           */
/********************************/

export class Gem {
  constructor(w, h, corner, loopCount, tableRatio, bufferSize = 2048) {
    this.width = w;
    this.height = h;
    this.aspectRatio = w / h;
    this.corner = corner;
    this.loopCount = loopCount;
    this.tableRatio = tableRatio;
    this.bufferSize = bufferSize;
    this.tableHeight = this.height * tableRatio;
    this.tableWidth = this.width - (this.height - this.tableHeight);
    this.loopsArray = [];
    this.quadsArray = [];
    this.verticesArray = [];
    this.uvsArray = [];
    this.createLoops();
    this.createQuads();
    this.populateVertices(this.quadsArray, this.verticesArray);
    this.populateUVs(this.quadsArray, this.uvsArray);
  }

  draw() {
    for (let f of this.quadsArray) {
      f.draw();
    }
  }

  setUVoffset(offset) {
    for (let f of this.quadsArray) {
      f.setUVoffset(offset);
    }
  }

  createLoops() {
    const totalDepth = this.width * 0.1;

    for (let i = 0; i <= this.loopCount; i++) {
      let loopLerp = (1.0 / this.loopCount) * i;
      let sizeLerp = loopLerp * 0.8 + 0.2 * easeOutSine(loopLerp); // make the loops smaller towards the center
      let loopWidth = lerp(this.tableWidth, this.width, sizeLerp);
      let loopHeight = lerp(this.tableHeight, this.height, sizeLerp);

      // Calculate z offset for the loop using a power function for curvature
      let zPos = totalDepth - totalDepth * Math.pow(i / this.loopCount, 1.52);

      let loopCorner = this.corner + this.corner * 0.2 * easeOutCirc(loopLerp); // make the corners curve towards the center

      this.loopsArray[i] = new Loop(loopWidth, loopHeight, loopCorner, zPos);
    }
  }

  createQuads() {
    // set the UVs to the center of the texture

    let w = 1.0; // normalize width to 1.0
    let h = w / this.aspectRatio; // calculate height based on aspect ratio

    let uvOffset = new p5.Vector(0, 0);
    let uvScale = 0.01;

    // create table (3 quads)
    let tp = this.loopsArray[0].vertices;
    let tableQuads = [
      [tp[0], tp[1], tp[2], tp[3]], // top
      [tp[0], tp[3], tp[4], tp[7]], // middle
      [tp[7], tp[4], tp[5], tp[6]], // bottom
    ];
    for (let i = 0; i < tableQuads.length; i++) {
      let q = new Quad(tableQuads[i], i);
      if (i > 0) q.setCenter(this.quadsArray[0].center); // set center to first quad
      // q.transformAndScaleUVs(createVector(w * 0.8, h * 0.5), 0, 0.002);
      q.scaleUVs(uvScale);
      q.offsetUVs(w * 0.5, 0.5);
      if (q) this.quadsArray[i] = q;
    }

    // create crown quads
    for (let i = 0; i < this.loopsArray.length - 1; i++) {
      // iterate through loops from the inside out
      let innerLoop = this.loopsArray[i];
      let outerLoop = this.loopsArray[i + 1];
      for (let j = 0; j < 8; j++) {
        // create the 8 quads between that loop and the next
        let quadIndex = 3 + i * 8 + j;
        let vertices = [
          innerLoop.vertices[j],
          outerLoop.vertices[j],
          outerLoop.vertices[(j + 1) % 8],
          innerLoop.vertices[(j + 1) % 8],
        ];
        let q = new Quad(vertices, quadIndex);
        // manipulate the UVs to add variation
        uvOffset.x += random() * j * this.bufferSize * 0.01; // add randomness to the horizontal offset
        uvOffset.y += random() * j * this.bufferSize * 0.01; // add randomness to the vertical offset
        let rotateUV = diceRoll(4) * PI * 0.5; // rotate UVs randomly
        q.offsetUVs(uvOffset.x, uvOffset.y);
        q.scaleUVs(uvScale);
        q.rotateUVs(rotateUV);
        this.quadsArray[quadIndex] = q; // add the quad to the array
      }
    }
  }

  populateVertices(quadsArray, vertexArray) {
    for (let q of quadsArray) {
      for (let v of q.getVertices()) {
        vertexArray.push(v);
        // console.log(v.x, v.y, v.z)
      }
    }
    return vertexArray;
  }

  populateUVs(quadsArray, uvsArray) {
    for (let q of quadsArray) {
      for (let uv of q.getUVs()) {
        uvsArray.push(uv);
        //console.log(v.x, v.y)
      }
    }
    return uvsArray;
  }

  getUVs() {
    return this.uvsArray;
  }

  getVertices() {
    return this.verticesArray;
  }

  getQuads() {
    return this.quadsArray;
  }
}
